import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { KanbanSquare, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { usePipelines } from '@/hooks/queries/useCrmPipelines';
import { useForecasting } from '@/hooks/queries/useCrmDashboard';
import { useOpportunites } from '@/hooks/queries/useCrmOpportunites';
import { formatDA, formatDACompact } from '@/lib/format';
import { t } from '@/locales/i18n';

export function CrmForecastingPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: pipelines = [] } = usePipelines(entrepriseId);
  const { data: users = [] } = useUsersList();
  const [pipelineId, setPipelineId] = useState('');
  const [commercialId, setCommercialId] = useState('');
  const [drillHorizon, setDrillHorizon] = useState<30 | 60 | 90>(30);

  const { data: forecast = [] } = useForecasting({
    entrepriseId,
    pipelineId: pipelineId || null,
    commercialId: commercialId || null,
  });

  const pipelineMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pipelines) m.set(p.id, p.nom);
    return m;
  }, [pipelines]);

  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);

  // Aggrégation par pipeline pour le graphique
  const chartData = useMemo(() => {
    const byPipeline = new Map<
      string,
      { pipeline: string; '30j': number; '60j': number; '90j': number }
    >();
    for (const f of forecast) {
      const key = f.pipeline_id;
      const cur = byPipeline.get(key) ?? {
        pipeline: f.pipeline_nom,
        '30j': 0,
        '60j': 0,
        '90j': 0,
      };
      if (f.horizon_jours === 30) cur['30j'] += Number(f.ca_pondere);
      if (f.horizon_jours === 60) cur['60j'] += Number(f.ca_pondere);
      if (f.horizon_jours === 90) cur['90j'] += Number(f.ca_pondere);
      byPipeline.set(key, cur);
    }
    return Array.from(byPipeline.values());
  }, [forecast]);

  const totals = useMemo(() => {
    const r = { 30: 0, 60: 0, 90: 0 };
    for (const f of forecast) {
      if (f.horizon_jours === 30) r[30] += Number(f.ca_pondere);
      if (f.horizon_jours === 60) r[60] += Number(f.ca_pondere);
      if (f.horizon_jours === 90) r[90] += Number(f.ca_pondere);
    }
    return r;
  }, [forecast]);

  // Drill-down : opportunités du pipeline sélectionné dans l'horizon
  const drillEnd = new Date(Date.now() + drillHorizon * 86400000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const { data: drillOpps } = useOpportunites({
    entrepriseId,
    pipelineId: pipelineId || null,
    statut: 'open',
    dateFrom: today,
    dateTo: drillEnd,
    pageSize: 100,
  });

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Forecasting"
        subtitle="CA prévisionnel pondéré par horizon, pipeline et commercial."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <select
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous pipelines —</option>
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
            </select>
            <select
              value={commercialId}
              onChange={(e) => setCommercialId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous commerciaux —</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.full_name ?? u.user_id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
        }
      />

      {entrepriseId && pipelines.length === 0 ? (
        <EmptyState
          icon={<KanbanSquare className="h-6 w-6" />}
          title={`Aucun pipeline configuré pour ${entreprises.find((e) => e.id === entrepriseId)?.nom ?? 'cette entreprise'}`}
          description="Le forecasting nécessite au moins un pipeline. Créez-en un pour démarrer."
          action={
            <Button asChild>
              <Link to={`/parametres/crm/pipelines?entreprise=${entrepriseId}`}>
                → Configurer un pipeline
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {([30, 60, 90] as const).map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setDrillHorizon(h)}
                className={`rounded-lg border bg-card p-3 text-left transition ${
                  drillHorizon === h ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                }`}
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <TrendingUp className="h-3.5 w-3.5" /> CA pondéré {h} jours
                </div>
                <div className="num text-2xl font-semibold">{formatDA(totals[h])}</div>
                <div className="text-[10px] text-muted-foreground">Cliquer pour drill-down</div>
              </button>
            ))}
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold">CA pondéré par pipeline et horizon</h3>
            <div className="h-72 w-full">
              {chartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Aucune donnée — opportunités sans date de clôture prévue ou pas d&apos;ouverture
                  en cours.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="pipeline" tick={{ fontSize: 10 }} />
                    <YAxis
                      tickFormatter={(v: number) => formatDACompact(v)}
                      tick={{ fontSize: 10 }}
                    />
                    <Tooltip formatter={(v: number) => formatDA(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="30j" fill="hsl(var(--primary))" />
                    <Bar dataKey="60j" fill="hsl(var(--success))" />
                    <Bar dataKey="90j" fill="hsl(var(--warning))" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold">
              Drill-down — opportunités à clôturer dans {drillHorizon} jours (
              {drillOpps?.count ?? 0} résultats)
            </h3>
            <div className="scrollbar-thin max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2">Réf</th>
                    <th className="py-2">Titre</th>
                    <th className="py-2">Pipeline</th>
                    <th className="py-2">Commercial</th>
                    <th className="py-2 text-right">Montant</th>
                    <th className="py-2 text-right">Prob.</th>
                    <th className="py-2 text-right">Pondéré</th>
                  </tr>
                </thead>
                <tbody>
                  {(drillOpps?.rows ?? []).map((o) => (
                    <tr key={o.id} className="border-b border-border/40">
                      <td className="py-1 font-mono">
                        <Link
                          to={`/crm/opportunite/${o.id}`}
                          className="text-primary hover:underline"
                        >
                          {o.reference}
                        </Link>
                      </td>
                      <td className="line-clamp-1 py-1">{o.titre}</td>
                      <td className="py-1">{pipelineMap.get(o.pipeline_id) ?? '—'}</td>
                      <td className="py-1">
                        {o.commercial_id ? userMap.get(o.commercial_id) : '—'}
                      </td>
                      <td className="num py-1 text-right">
                        {formatDA(Number(o.montant_estime_dzd))}
                      </td>
                      <td className="num py-1 text-right">{o.probabilite}%</td>
                      <td className="num py-1 text-right font-medium">
                        {formatDA((Number(o.montant_estime_dzd) * o.probabilite) / 100)}
                      </td>
                    </tr>
                  ))}
                  {(drillOpps?.rows ?? []).length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-muted-foreground">
                        Aucune opportunité à clôturer dans cet horizon.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <span className="hidden">{t('crm.kpi.ca30j')}</span>
    </div>
  );
}
